import React, { useState, useRef, useEffect } from 'react';
import { X, Phone, MessageSquare, Mail, Linkedin, CheckCircle, AlertCircle, Circle, AlertTriangle } from 'lucide-react';
import { executeTest } from '../../services/testAgentService';
import { useAuth } from '../../contexts/AuthContext';
import { useTestAgentReadiness } from '../../hooks/useOutreachReadiness';
import { CallProgressSteps } from './CallProgressSteps';
import { WhatsAppProgressSteps } from './WhatsAppProgressSteps';
import { EmailProgressSteps } from './EmailProgressSteps';
import { LinkedInProgressSteps } from './LinkedInProgressSteps';

type AgentType = 'call' | 'whatsapp' | 'email' | 'linkedin';

interface TestAgentModalProps {
  isOpen: boolean;
  onClose: () => void;
  agentType?: AgentType;
}

const AGENT_CONFIG: Record<AgentType, {
  icon: typeof Phone;
  title: string;
  subtitle: string;
  label: string;
  placeholder: string;
  inputType: string;
  description: string;
  successTitle: string;
  successMessage: string;
  buttonText: string;
  loadingText: string;
}> = {
  call: {
    icon: Phone,
    title: 'Test Call Agent',
    subtitle: 'Receive a live test call from your AI agent',
    label: 'Phone Number',
    placeholder: '+1 (555) 123-4567',
    inputType: 'tel',
    description: 'Enter your phone number to receive a test call from the AI agent.',
    successTitle: 'Test Call Initiated!',
    successMessage: 'You should receive a call shortly.',
    buttonText: 'Start Test Call',
    loadingText: 'Initiating...',
  },
  whatsapp: {
    icon: MessageSquare,
    title: 'Test WhatsApp Agent',
    subtitle: 'Receive a test WhatsApp message from your AI agent',
    label: 'Phone Number',
    placeholder: '+1 (555) 123-4567',
    inputType: 'tel',
    description: 'Enter your phone number to receive a test WhatsApp message.',
    successTitle: 'Test Message Sent!',
    successMessage: 'You should receive a WhatsApp message shortly.',
    buttonText: 'Send Test Message',
    loadingText: 'Sending...',
  },
  email: {
    icon: Mail,
    title: 'Test Email Agent',
    subtitle: 'Receive a test email from your AI agent',
    label: 'Email Address',
    placeholder: 'test@example.com',
    inputType: 'email',
    description: 'Enter your email address to receive a test email from the AI agent.',
    successTitle: 'Test Email Sent!',
    successMessage: 'Check your inbox shortly.',
    buttonText: 'Send Test Email',
    loadingText: 'Sending...',
  },
  linkedin: {
    icon: Linkedin,
    title: 'Test LinkedIn Agent',
    subtitle: 'Send a test LinkedIn message from your AI agent',
    label: 'LinkedIn Profile URL',
    placeholder: 'https://linkedin.com/in/username',
    inputType: 'text',
    description: 'Enter a LinkedIn profile URL to send a test welcome message.',
    successTitle: 'Test Message Sent!',
    successMessage: 'The LinkedIn welcome message has been queued.',
    buttonText: 'Send Test Message',
    loadingText: 'Sending...',
  },
};

type ModalPhase = 'setup' | 'idle' | 'progress' | 'success' | 'error';

const BODY_HEIGHT = 340;

const AGENT_LABEL_MAP: Record<AgentType, string> = {
  call: 'Call',
  whatsapp: 'WhatsApp',
  email: 'Email',
  linkedin: 'LinkedIn',
};

const TestAgentModal: React.FC<TestAgentModalProps> = ({ isOpen, onClose, agentType = 'call' }) => {
  const { user } = useAuth();
  const { ready: setupReady, loading: setupLoading, categories, check: checkSetup } = useTestAgentReadiness(user?.client_id, agentType);

  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [phase, setPhase] = useState<ModalPhase>('idle');
  const [apiDone, setApiDone] = useState(false);
  const [apiFailed, setApiFailed] = useState(false);
  const [visiblePhase, setVisiblePhase] = useState<ModalPhase>('idle');
  const [transitioning, setTransitioning] = useState(false);
  const [resultType, setResultType] = useState<string | undefined>();
  const [setupChecked, setSetupChecked] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isOpen && !setupChecked) {
      setSetupChecked(true);
      checkSetup();
    }
    if (!isOpen) {
      setSetupChecked(false);
    }
  }, [isOpen, setupChecked, checkSetup]);

  useEffect(() => {
    if (!setupChecked || setupLoading) return;
    if (!setupReady && (phase === 'idle' || phase === 'setup')) {
      setPhase('setup');
      setVisiblePhase('setup');
    }
  }, [setupChecked, setupLoading, setupReady, phase]);

  useEffect(() => {
    if (phase === visiblePhase) return;
    setTransitioning(true);
    const timer = setTimeout(() => {
      setVisiblePhase(phase);
      setTransitioning(false);
    }, 250);
    return () => clearTimeout(timer);
  }, [phase, visiblePhase]);

  if (!isOpen) return null;

  const config = AGENT_CONFIG[agentType];
  const Icon = config.icon;

  const validateInput = (value: string): boolean => {
    if (agentType === 'call' || agentType === 'whatsapp') {
      const cleaned = value.replace(/\D/g, '');
      return cleaned.length >= 10 && cleaned.length <= 15;
    }
    if (agentType === 'email') {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }
    if (agentType === 'linkedin') {
      return value.trim().length > 5;
    }
    return false;
  };

  const formatInput = (value: string): string => {
    if (agentType === 'call' || agentType === 'whatsapp') {
      const cleaned = value.replace(/\D/g, '');
      if (cleaned.length === 10) return `1${cleaned}`;
      return cleaned;
    }
    return value.trim();
  };

  const resetState = () => {
    setInput('');
    setError('');
    setPhase('idle');
    setVisiblePhase('idle');
    setTransitioning(false);
    setIsLoading(false);
    setApiDone(false);
    setApiFailed(false);
    setResultType(undefined);
    setSetupChecked(false);
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateInput(input)) {
      const errorMessages: Record<AgentType, string> = {
        call: 'Please enter a valid phone number (10-15 digits)',
        whatsapp: 'Please enter a valid phone number (10-15 digits)',
        email: 'Please enter a valid email address',
        linkedin: 'Please enter a valid LinkedIn profile URL',
      };
      setError(errorMessages[agentType]);
      return;
    }

    setIsLoading(true);
    setPhase('progress');
    setApiDone(false);
    setApiFailed(false);

    try {
      const formatted = formatInput(input);
      const result = await executeTest(agentType, formatted);

      if (result.success) {
        setResultType(result.type);
        setApiDone(true);
        closeTimerRef.current = setTimeout(() => {
          setPhase('success');
          closeTimerRef.current = setTimeout(() => {
            onClose();
            resetState();
          }, 2500);
        }, 1200);
      } else {
        setApiFailed(true);
        setError(result.message);
        setPhase('error');
      }
    } catch (err) {
      setApiFailed(true);
      setError('Failed to send test. Please try again.');
      setPhase('error');
      console.error('Test agent error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (phase === 'progress') return;
    onClose();
    resetState();
  };

  const handleRetry = () => {
    setError('');
    setPhase('idle');
    setApiDone(false);
    setApiFailed(false);
    setIsLoading(false);
  };

  const isInProgress = visiblePhase === 'progress';

  const renderIdleContent = () => (
    <form onSubmit={handleSubmit} className="flex flex-col h-full">
      <div className="flex-1 flex flex-col justify-center">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {config.label}
          </label>
          <div className="relative">
            <Icon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type={config.inputType}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={config.placeholder}
              className="w-full pl-10 pr-4 py-3 border-2 border-[#1875FF]/20 rounded-lg focus:ring-2 focus:ring-[#05E0D1] focus:border-[#1875FF] transition-all"
              disabled={isLoading}
            />
          </div>
          <p className="mt-2 text-sm text-gray-600">{config.description}</p>
        </div>

        {error && (
          <div className="flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg mt-4">
            <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}
      </div>

      <div className="flex space-x-3 pt-4">
        <button
          type="button"
          onClick={handleClose}
          disabled={isLoading}
          className="flex-1 px-4 py-3 border-2 border-[#1875FF]/20 rounded-lg font-medium text-gray-700 hover:bg-[#1875FF]/5 hover:border-[#1875FF]/40 transition-all disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading || !input}
          className="flex-1 px-4 py-3 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white rounded-lg font-medium hover:shadow-lg hover:shadow-[#05E0D1]/30 hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center space-x-2"
        >
          {isLoading ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>{config.loadingText}</span>
            </>
          ) : (
            <>
              <Icon size={20} />
              <span>{config.buttonText}</span>
            </>
          )}
        </button>
      </div>
    </form>
  );

  const progressComponents: Record<AgentType, React.ElementType> = {
    call: CallProgressSteps,
    whatsapp: WhatsAppProgressSteps,
    email: EmailProgressSteps,
    linkedin: LinkedInProgressSteps,
  };

  const renderProgressContent = () => {
    const ProgressComponent = progressComponents[agentType];
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <ProgressComponent apiDone={apiDone} apiFailed={apiFailed} />
      </div>
    );
  };

  const renderSuccessContent = () => {
    const isConnectionRequest = agentType === 'linkedin' && resultType === 'connection_request';
    const title = isConnectionRequest ? 'Connection Request Sent!' : config.successTitle;
    const message = isConnectionRequest
      ? 'Once they accept your request, you can send follow-up messages.'
      : config.successMessage;

    return (
      <div className="flex flex-col items-center justify-center h-full">
        <div className="w-16 h-16 bg-gradient-to-br from-[#1875FF]/20 to-[#05E0D1]/20 rounded-full flex items-center justify-center mb-4">
          <CheckCircle className="text-[#05E0D1]" size={32} />
        </div>
        <h4 className="text-lg font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-[#1875FF] to-[#05E0D1] font-display">{title}</h4>
        <p className="text-sm text-gray-500 mt-2 text-center max-w-xs">{message}</p>
      </div>
    );
  };

  const renderErrorContent = () => (
    <div className="flex flex-col items-center justify-center h-full">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-red-50 mb-4">
        <AlertCircle className="w-7 h-7 text-red-500" />
      </div>
      <h4 className="text-base font-bold text-gray-900 mb-1">Something went wrong</h4>
      <p className="text-sm text-gray-500 mb-6 text-center max-w-xs">{error || 'An unexpected error occurred.'}</p>
      <button
        onClick={handleRetry}
        className="px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-[#1875FF] to-[#05E0D1] rounded-xl hover:shadow-lg transition-all"
      >
        Try Again
      </button>
      <button
        onClick={handleClose}
        className="text-sm text-gray-400 hover:text-gray-600 transition-colors mt-3"
      >
        Cancel
      </button>
    </div>
  );

  const renderSetupContent = () => (
    <div className="flex flex-col items-center justify-center h-full">
      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 flex items-center justify-center mb-4">
        <AlertTriangle className="w-7 h-7 text-[#1875FF]" />
      </div>
      <h4 className="text-base font-bold text-gray-900 mb-1">Complete Setup First</h4>
      <p className="text-sm text-gray-500 mb-5 text-center max-w-xs">
        Before testing your {AGENT_LABEL_MAP[agentType]} Agent, complete the following:
      </p>
      <div className="w-full max-w-xs space-y-3 mb-6">
        {categories.map((cat) => (
          <div key={cat.label} className="flex items-center gap-3">
            {cat.ready ? (
              <CheckCircle className="w-5 h-5 text-[#05E0D1] flex-shrink-0" />
            ) : (
              <Circle className="w-5 h-5 text-gray-300 flex-shrink-0" />
            )}
            <span className={`text-sm ${cat.ready ? 'text-gray-400 line-through' : 'text-gray-700 font-medium'}`}>
              {cat.label}
            </span>
          </div>
        ))}
      </div>
      <button
        onClick={handleClose}
        className="px-8 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-[#1875FF] to-[#05E0D1] rounded-xl hover:shadow-lg hover:shadow-[#05E0D1]/25 transition-all"
      >
        Got It
      </button>
    </div>
  );

  const renderLoadingContent = () => (
    <div className="flex flex-col items-center justify-center h-full">
      <div className="w-8 h-8 border-3 border-[#1875FF]/20 border-t-[#1875FF] rounded-full animate-spin" />
    </div>
  );

  const phaseRenderers: Record<ModalPhase, () => React.ReactNode> = {
    setup: renderSetupContent,
    idle: renderIdleContent,
    progress: renderProgressContent,
    success: renderSuccessContent,
    error: renderErrorContent,
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-xl max-w-md w-full shadow-2xl animate-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between p-6 border-b border-[#05E0D1]/20 bg-gradient-to-r from-[#1875FF]/5 to-[#05E0D1]/5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1875FF] to-[#05E0D1] flex items-center justify-center shadow-lg flex-shrink-0">
              <Icon className="text-white" size={24} />
            </div>
            <div>
              <h3 className="text-xl font-extrabold text-gray-900 tracking-tight font-display">{config.title}</h3>
              <p className="text-sm text-gray-600 mt-0.5">{config.subtitle}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isInProgress}
            className="text-gray-400 hover:text-[#1875FF] transition-colors disabled:opacity-30 disabled:cursor-not-allowed p-2 hover:bg-white rounded-lg"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6" style={{ height: BODY_HEIGHT }}>
          {setupLoading ? (
            renderLoadingContent()
          ) : (
            <div
              className={`h-full transition-opacity duration-250 ${transitioning ? 'opacity-0' : 'opacity-100'}`}
            >
              {phaseRenderers[visiblePhase]()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestAgentModal;
